import {forwardRef} from 'react'
import Svg, {Path} from 'react-native-svg'

import {type Props, useCommonSVGProps} from '#/components/icons/common'

/**
 * A capsule with a plus cut through it, flanked by the edges of the capsules
 * behind it.
 *
 * The artwork is drawn as a filled glyph rather than in the stroke set the rest
 * of the icons come from — the plus is a hole punched in the card, and the two
 * faint slivers on either side are the cards it sits on top of. The slivers are
 * drawn at their own opacities, so it is a component instead of a `createSVG`
 * one-liner.
 *
 * The path data is kept exactly as authored: it is written against a viewBox
 * translated by (-252.84, -527.22), so that translation is folded into the
 * viewBox origin rather than rewritten into every coordinate. That way the
 * numbers still match the file it came from if it ever needs redrawing.
 */
export const AddCapsule = forwardRef<Svg, Props>(
  function AddCapsuleImpl(props, ref) {
    const {fill, size, style, ...rest} = useCommonSVGProps(props)

    return (
      <Svg
        fill="none"
        {...rest}
        ref={ref}
        viewBox="252.84 527.22 117.59 112.11"
        width={size}
        height={size}
        style={[style]}>
        {/* The cards behind the card, nearest first, fading as they recede. */}
        <Path
          fill={fill}
          opacity={0.7}
          d="M268.57,540.37h-1.52a4.3,4.3,0,0,0-4.38,4.23v77.34a4.3,4.3,0,0,0,4.38,4.23h1.52Z"
        />
        <Path
          fill={fill}
          opacity={0.3}
          d="M257.22,621.26a4.33,4.33,0,0,1-4.38-4.27V549.56a4.32,4.32,0,0,1,4.38-4.27h1.52v76Z"
        />
        <Path
          fill={fill}
          opacity={0.7}
          d="M354.7,626.17h1.52a4.3,4.3,0,0,0,4.38-4.23V544.6a4.3,4.3,0,0,0-4.38-4.23H354.7Z"
        />
        <Path
          fill={fill}
          opacity={0.3}
          d="M364.53,621.26v-76h1.53a4.32,4.32,0,0,1,4.37,4.27V617a4.33,4.33,0,0,1-4.37,4.27Z"
        />
        {/*
          The card itself, with the plus as a hole: `evenodd` is what knocks
          it through, so the surface behind the icon shows in the cross.
        */}
        <Path
          fill={fill}
          fillRule="evenodd"
          clipRule="evenodd"
          d="M350.38,555.2c0-3.61,0-7.23,0-10.84a16.79,16.79,0,0,0-16.9-17.07q-21.74-.12-43.45,0a16.9,16.9,0,0,0-17,17.24c0,14.3,0,63.28,0,77.58a16.93,16.93,0,0,0,17.23,17.24q21.45,0,42.91,0a16.85,16.85,0,0,0,17.18-17.25c0-7.11,0-14.23,0-21.34h0C350.38,597.1,350.37,558.81,350.38,555.2Zm-22.77,32.63H316.2v11.42a4.57,4.57,0,0,1-9.13,0V587.83H295.66a4.56,4.56,0,1,1,0-9.12h11.41V567.3a4.57,4.57,0,1,1,9.13,0v11.41h11.41a4.56,4.56,0,0,1,0,9.12Z"
        />
      </Svg>
    )
  },
)
